import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { Link as LinkDeRota } from 'react-router-dom';

import { EstadoDeCarregamento } from '../../shared/ui/EstadoDeCarregamento';
import { rotuloDoEstado } from './etapas';
import { SeloDaSituacaoDoPrazo } from './SeloDaSituacaoDoPrazo';
import { identificacaoDoCaso, idCurto, lerRelogio } from './leitura';
import { EstadoDeErro } from '../../shared/ui/EstadoDeErro';
import { EstadoVazio } from '../../shared/ui/EstadoVazio';
import { Identificador } from '../../shared/ui/Identificador';
import { ORIGENS_DO_CASO, caminhoDoCaso } from '../../shared/navegacao/mapaDeNavegacao';
import { formatarDataHora } from '../../shared/dominio/datas';
import { useFiltrosNaUrl } from '../../shared/hooks/useFiltrosNaUrl';
import { TAMANHO_DE_PAGINA, opcoesDaFilaDeCasos } from './api/casos';
import {
  ESTADOS_DO_CASO,
  ROTULOS_CURTOS_DA_RAZAO_DA_IMPLANTACAO,
  ROTULOS_DE_ESTADO,
  ROTULOS_DO_TIPO_DE_ESPERA,
  ROTULO_DO_PACIENTE_PROVISORIO,
  marcaDoVinculoIw,
  rotuloDoDono,
  type CasoDaFila,
} from './api/tipos';

/** `caso.espera.abertas`, em texto; vazio ou ausente vira travessão — nada se finge de dado. */
function rotuloDaEspera(caso: CasoDaFila): string {
  if (caso.espera === null || caso.espera.abertas.length === 0) return '—';
  return caso.espera.abertas.map((tipo) => ROTULOS_DO_TIPO_DE_ESPERA[tipo]).join(' · ');
}

/**
 * O relógio que está correndo AGORA no Caso (resposta, implantação, ou nenhum), o mesmo que o cartão da captação mostra:
 * ambos leem `lerRelogio`. A situação (`Vencido`, `Perto de estourar`...) é a que o servidor calculou. O Caso sem prazo
 * diz por quê, nunca uma célula vazia nem um número padrão; o detalhe do Caso tem os dois relógios por inteiro.
 */
function PrazoAtual({ caso }: { caso: CasoDaFila }): React.JSX.Element {
  const relogio = lerRelogio(caso);
  const semPrazoDeImplantacao = relogio.rotulo === 'Implantação' && relogio.prazo === null;
  return (
    <>
      <Typography variant="body2">{relogio.rotulo}</Typography>
      {relogio.situacao === null ? null : <SeloDaSituacaoDoPrazo situacao={relogio.situacao} />}
      {relogio.prazo === null ? null : <> prazo {formatarDataHora(relogio.prazo)}</>}
      <Typography variant="body2" color="text.secondary">
        {semPrazoDeImplantacao
          ? ROTULOS_CURTOS_DA_RAZAO_DA_IMPLANTACAO[caso.razaoDaImplantacao]
          : relogio.prazo === null
            ? relogio.texto
            : relogio.contagem}
      </Typography>
    </>
  );
}

/**
 * A fila de casos.
 *
 * Quatro estados, todos distintos e todos testados: carregando, erro, vazio
 * e dados. A separação entre os dois do meio é a regra mais dura deste
 * arquivo — com a API fora do ar a tela mostra ERRO, com caminho de
 * reprocesso, e nunca uma lista vazia. Apresentar indisponibilidade como
 * "sem pendências" é o bug que a doc proíbe nominalmente.
 *
 * As colunas são os campos que `CaseListItem` entrega, incluindo os dois relógios; o que não tem prazo diz por quê.
 * Protocolo, operadora e beneficiário ficam de fora: coluna com placeholder ensina a confiar num dado que não existe.
 */
export function ListaDeCasos(): React.JSX.Element {
  const { filtros, definirEstado, definirPagina, limparFiltros, temFiltroAtivo } = useFiltrosNaUrl();
  const consulta = useQuery(opcoesDaFilaDeCasos(filtros.pagina));

  // O filtro é do FRONT e só alcança a página carregada — `GET /casos` não
  // aceita filtro por estado. O rótulo do campo diz isso com todas as
  // letras, para ninguém ler "nenhum resultado" como "não existe nenhum".
  const casos = consulta.data ?? [];
  const visiveis = filtros.estado === '' ? casos : casos.filter((caso) => caso.estado === filtros.estado);

  // Sem `total` no corpo, "tem próxima página" só pode ser INFERIDO: página
  // cheia é indício de que há mais. É o que a API permite saber.
  const talvezTenhaProxima = casos.length === TAMANHO_DE_PAGINA;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h1">Casos</Typography>
        <Typography variant="body2" color="text.secondary">
          Todos os Casos do seu núcleo, em andamento ou não. É onde se trabalha cada um.
        </Typography>
      </Box>


      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <TextField
          select
          label="Estado (filtra esta página)"
          value={filtros.estado}
          onChange={(evento) => {
            definirEstado(evento.target.value);
          }}
          sx={{ minWidth: 260 }}
        >
          <MenuItem value="">Todos</MenuItem>
          {ESTADOS_DO_CASO.map((estado) => (
            <MenuItem key={estado} value={estado}>
              {ROTULOS_DE_ESTADO[estado]}
            </MenuItem>
          ))}
        </TextField>
      </Paper>

      <Paper variant="outlined">
        {consulta.isPending ? <EstadoDeCarregamento /> : null}

        {/* Erro vem ANTES de qualquer checagem de lista vazia. Invertendo
            a ordem, uma falha viraria "nenhum caso encontrado". */}
        {consulta.isError ? (
          <EstadoDeErro
            erro={consulta.error}
            contexto="Não foi possível carregar a fila de casos"
            aoReprocessar={() => {
              void consulta.refetch();
            }}
          />
        ) : null}

        {consulta.isSuccess && visiveis.length === 0 ? (
          <EstadoVazio
            titulo={temFiltroAtivo ? 'Nenhum caso com esse filtro' : 'Nenhum caso na fila'}
            descricao={
              temFiltroAtivo
                ? 'A API respondeu normalmente; nenhum caso desta página está nesse estado.'
                : 'A API respondeu normalmente e não há caso nesta página.'
            }
            {...(temFiltroAtivo ? { acao: { rotulo: 'Limpar filtros', aoClicar: limparFiltros } } : {})}
          />
        ) : null}

        {consulta.isSuccess && visiveis.length > 0 ? (
          <TableContainer>
            <Table aria-label="Fila de casos">
              <TableHead>
                <TableRow>
                  <TableCell>Caso</TableCell>
                  <TableCell>Paciente</TableCell>
                  <TableCell>Cadastro</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Espera</TableCell>
                  <TableCell>Dono</TableCell>
                  <TableCell>Prazo atual</TableCell>
                  <TableCell>Criado em</TableCell>
                  <TableCell>Atualizado em</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visiveis.map((caso) => (
                  <TableRow key={caso.id} hover>
                    <TableCell>
                      <LinkDeRota
                        to={caminhoDoCaso(caso.id)}
                        state={{ origem: ORIGENS_DO_CASO.fila }}
                        style={{ textDecoration: 'none' }}
                        title={caso.id}
                        aria-label={`${identificacaoDoCaso(caso)} — ${caso.pacienteNome ?? idCurto(caso.pacienteId)}`}
                      >
                        <Typography variant="body2" component="span">
                          {identificacaoDoCaso(caso)}
                        </Typography>
                      </LinkDeRota>
                    </TableCell>
                    <TableCell>
                      {caso.pacienteNome === null ? (
                        <Identificador valor={caso.pacienteId} />
                      ) : (
                        <Typography variant="body2" title={caso.pacienteId}>
                          {caso.pacienteNome}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {caso.pacienteProvisorio ? (
                        <Typography variant="body2">
                          {marcaDoVinculoIw(caso) ?? `Paciente ${ROTULO_DO_PACIENTE_PROVISORIO}`}
                        </Typography>
                      ) : null}
                      <Typography variant="body2" color="text.secondary">
                        {caso.pendenciasAbertas === 0
                          ? 'Nenhuma pendência aberta'
                          : `${String(caso.pendenciasAbertas)} ${caso.pendenciasAbertas === 1 ? 'pendência aberta' : 'pendências abertas'}`}
                      </Typography>
                    </TableCell>
                    <TableCell>{rotuloDoEstado(caso.estado)}</TableCell>
                    <TableCell>{rotuloDaEspera(caso)}</TableCell>
                    <TableCell>{rotuloDoDono(caso)}</TableCell>
                    <TableCell>
                      <PrazoAtual caso={caso} />
                    </TableCell>
                    <TableCell>{formatarDataHora(caso.criadoEm)}</TableCell>
                    <TableCell>{formatarDataHora(caso.atualizadoEm)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}
      </Paper>

      {/* Sem total no contrato, não há "página 3 de 7" — só o número da
          página atual e um "próxima" que existe enquanto a página vier
          cheia. Inventar o total seria repetir o erro que este ciclo
          desfez. */}
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Button
          variant="outlined"
          disabled={filtros.pagina <= 1}
          onClick={() => {
            definirPagina(filtros.pagina - 1);
          }}
        >
          Página anterior
        </Button>
        <Typography variant="body2" color="text.secondary">
          Página {filtros.pagina}
        </Typography>
        <Button
          variant="outlined"
          disabled={!talvezTenhaProxima}
          onClick={() => {
            definirPagina(filtros.pagina + 1);
          }}
        >
          Próxima página
        </Button>
      </Stack>
    </Stack>
  );
}
